import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { withEntryQuery } from "../../entryParams";
import {
  IMPRINT_URL,
  PRIVACY_URL,
  TERMS_URL,
  WITHDRAWAL_POLICY_URL,
} from "../../externalLinks";
import { CANCELLATION_PATH, DSA_PATH, WITHDRAWAL_PATH } from "../../routes";

interface LegalLinksProps {
  /** Class of the surrounding nav. */
  navClassName?: string;
  /** Class every entry carries. */
  linkClassName?: string;
}

/**
 * The links this app owes every visitor, logged in or not.
 *
 * ⚠️ A component and not markup in the footer because it has two homes: the page
 * an unauthenticated visitor lands on is the login screen, which renders without
 * the app chrome and therefore without the footer, and § 312k Abs. 2 BGB, § 356a
 * BGB and Art. 16 DSA all want their route reachable from there too.
 *
 * The imprint, terms, privacy notice and withdrawal instructions stay on
 * lyra.rocks; only the three forms this app serves are routed internally. All four
 * belong here per Art. 246a EGBGB.
 */
function LegalLinks({
  navClassName = "footer-links",
  linkClassName = "footer-imprint",
}: LegalLinksProps) {
  const { t } = useTranslation();

  return (
    <nav className={navClassName} aria-label={t("app.footer.linksAria")}>
      <Link to={withEntryQuery(CANCELLATION_PATH)} className={linkClassName}>
        {t("app.footer.cancellation")}
      </Link>
      {/*
        § 356a BGB wants the withdrawal function as easy to find as the
        cancellation, so it sits right beside it.
      */}
      <Link to={withEntryQuery(WITHDRAWAL_PATH)} className={linkClassName}>
        {t("app.footer.withdraw")}
      </Link>
      <Link to={withEntryQuery(DSA_PATH)} className={linkClassName}>
        {t("app.footer.dsa")}
      </Link>
      <a
        href={IMPRINT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {t("app.footer.imprint")}
      </a>
      {/*
        The privacy notice belongs beside the imprint on every page: the public
        forms collect a name, an address and free text from people who have no
        account, and Art. 13 GDPR is owed at collection time.
      */}
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {t("app.footer.privacy")}
      </a>
      <a
        href={TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {t("app.footer.terms")}
      </a>
      <a
        href={WITHDRAWAL_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {t("app.footer.withdrawal")}
      </a>
    </nav>
  );
}

export default LegalLinks;
